import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Typography,
  Alert
} from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config from '../../../config';

const apiBaseUrl = config.getApiUrl();

const hasAcceptedAgreement = (user) => Boolean(user?.baseCampUserAgreement?.acceptedAt);

const BaseCampUserAgreementGate = ({ children }) => {
  const { user, refreshUser, logout, baseCampUserAgreementTerms } = useAuth();
  const [terms, setTerms] = useState(null);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const termsFetchStarted = useRef(false);
  const termsUsernameRef = useRef(null);

  const agreementAccepted = Boolean(user?.baseCampUserAgreement?.acceptedAt);
  const needsAgreement = Boolean(
    user && user.role !== 'super' && !agreementAccepted
  );

  useEffect(() => {
    if (!needsAgreement) {
      termsFetchStarted.current = false;
      termsUsernameRef.current = null;
      setLoadingTerms(false);
      return;
    }

    if (user?.username !== termsUsernameRef.current) {
      termsUsernameRef.current = user?.username ?? null;
      termsFetchStarted.current = false;
      setTerms(null);
      setLoadingTerms(true);
    }

    if (baseCampUserAgreementTerms) {
      setTerms(baseCampUserAgreementTerms);
      setLoadingTerms(false);
      return;
    }

    if (termsFetchStarted.current) {
      return;
    }
    termsFetchStarted.current = true;

    let cancelled = false;
    const loadTerms = async () => {
      try {
        const data = await fetchWithAuth(`${apiBaseUrl}/api/auth/me`);
        if (!cancelled) {
          setTerms(data.baseCampUserAgreementTerms || null);
        }
      } catch (err) {
        termsFetchStarted.current = false;
        if (!cancelled) {
          setError(err.message || 'Failed to load agreement');
        }
      } finally {
        if (!cancelled) {
          setLoadingTerms(false);
        }
      }
    };
    loadTerms();
    return () => {
      cancelled = true;
    };
  }, [needsAgreement, baseCampUserAgreementTerms, user?.username]);

  if (!user) {
    return null;
  }

  if (user.role === 'super' || hasAcceptedAgreement(user)) {
    return children;
  }

  const handleAccept = async () => {
    if (!checked || !terms?.version) return;
    setSubmitting(true);
    setError('');
    try {
      await fetchWithAuth(`${apiBaseUrl}/api/auth/accept-basecamp-agreement`, {
        method: 'POST',
        body: JSON.stringify({ agreementVersion: terms.version })
      });
      await refreshUser();
    } catch (err) {
      setError(err.message || 'Failed to save your agreement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open fullScreen disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 700, color: '#FF6B35' }}>
        BaseCamp User Agreement
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You must read and accept this agreement before using BaseCamp. Access is blocked until you confirm.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loadingTerms ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#FF6B35' }} />
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              maxHeight: '50vh',
              overflow: 'auto',
              bgcolor: '#FAFAFA',
              whiteSpace: 'pre-wrap',
              fontSize: '0.9rem',
              lineHeight: 1.6
            }}
          >
            {terms?.text || 'Agreement text is unavailable. Please contact an administrator.'}
          </Paper>
        )}
        <FormControlLabel
          sx={{ mt: 2, alignItems: 'flex-start' }}
          control={
            <Checkbox
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
              disabled={loadingTerms || !terms?.version}
            />
          }
          label="I have read this agreement and agree that I am bound by its terms. I will not use any information from BaseCamp outside YoungPresso."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button color="inherit" onClick={logout} disabled={submitting}>
          Sign out
        </Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!checked || submitting || loadingTerms || !terms?.version}
          sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#e55a2b' } }}
        >
          {submitting ? 'Saving…' : 'I agree — continue to BaseCamp'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BaseCampUserAgreementGate;
